import API from 'helpers/api';
import { apiURL } from 'config/baseURL';

/**
 * The signed-in user's own profile, served from Mongo by api-users.
 *
 * The plain route rather than /populated: the profile needs eight scalar fields, and
 * /populated additionally resolves departments and sites, which no profile consumer reads.
 *
 * The id token is a required parameter rather than resolved here via
 * `API.getFirebaseToken()`. This runs during auth resolution, before
 * `auth().currentUser` is populated, so that helper returns `''` and the request goes
 * out unauthenticated — a 401 that empties the profile and signs the session straight
 * back out. The caller holds the resolved Firebase user, so it can mint a real token.
 */
export const fetchOwnProfile = async (userID: string, token: string): Promise<Record<string, any>> => {
	if (!token) {
		throw new Error('profile fetch attempted without an auth token');
	}

	const response = await API.get(`${apiURL}/users/${userID}`, token);
	// A gateway 5xx serves HTML, not JSON. Tolerating an unparseable body keeps the thrown
	// error carrying the status instead of an opaque syntax error; on a 200 it leaves `data`
	// undefined, which the saga turns into a failure. Same shape as issuePdf.service.ts.
	const json = await response.json().catch(() => ({}));

	if (response.status !== 200) {
		throw new Error(json?.message || `profile fetch failed (${response.status})`);
	}

	const data = json.data;
	if (!data) {
		return data;
	}

	// Mongo names the tenant key `organizationID`; RTDB `/user/{uid}` called the same value
	// `organization`, and that is the name every consumer reads — including the auth gate
	// `!profile.organization`, which signs the session out when it is missing. Mapped here at
	// the API boundary so the store holds the shape consumers already expect.
	return { ...data, organization: data.organization ?? data.organizationID };
};
